/* WebComponentNewsletterSignup component */
/* eslint-disable @typescript-eslint/no-unsafe-declaration-merging -- disabled to allow to inherit from AbstractComponent */
import { Component, Element, Event, EventEmitter, h, Prop, State } from '@stencil/core'; // eslint-disable-line @typescript-eslint/no-unused-vars
import NewsletterSignupV2Template from './templates/cwc-newsletter-signup-v2.template';
import { AbstractComponent, FontFaces, Slot } from '@cewe-designsystem/abstract';
import { SignUpState } from './types/signup-state.enum';
import { FormState } from './types/form-state.enum';
import { ButtonState } from './types/button-state.enum';
import UtilNewsletterApi, { STATES, UtilNewsletterApiOptions } from '@cewe-designsystem/util_newsletter-api';

const EMAIL_INPUT_VALIDATION_DELAY: number = 1000;

interface NewsletterSignupEventEmitData {
  element: object;
  usage: string;
}

@Component({
  tag: 'cwc-newsletter-signup-v2',
  styleUrl: './cwc-newsletter-signup-v2.scss',
  shadow: true,
})
export class NewsletterSignupV2Component {
  /**
   * email input validation delay in milliseconds
   */
  static EMAIL_INPUT_DELAY: number = EMAIL_INPUT_VALIDATION_DELAY;

  /**
   * element
   */
  @FontFaces()
  @Element()
  element: HTMLElement;

  /**
   * slots
   */
  @Slot('initial-headline') initialHeadline;
  @Slot('initial-subheadline') initialSubheadline;
  @Slot('initial-text') initialText;
  @Slot('initial-subtext') initialSubtext;
  @Slot('initial-illustration') initialIllustration;
  @Slot('successful-headline') successfulHeadline;
  @Slot('successful-subheadline') successfulSubheadline;
  @Slot('successful-text') successfulText;
  @Slot('successful-illustration') successfulIllustration;
  @Slot('failed-headline') failedHeadline;
  @Slot('failed-subheadline') failedSubheadline;
  @Slot('failed-text') failedText;
  @Slot('failed-illustration') failedIllustration;

  /**
   * key account id
   */
  @Prop() keyAccountId: string;

  /**
   * Endpoint URL for newsletter API
   */
  @Prop() newsletterApiEndpoint: string;

  /**
   * language
   */
  @Prop() language: string;

  /**
   * locale
   */
  @Prop() locale: string;

  /**
   * referrer
   */
  @Prop() referrer: string;

  /**
   * placeholder text for input element
   */
  @Prop() placeholderText: string;

  /**
   * form button text
   */
  @Prop() buttonCtaText: string;

  /**
   * form button retry text
   */
  @Prop() buttonRetryText: string;

  /**
   * form button contact text
   */
  @Prop() buttonContactText: string;

  /**
   * form button contact URL
   */
  @Prop() buttonContactUrl: string;

  /**
   * form button contact target
   */
  @Prop() buttonContactTarget: string;

  /**
   * form invalid text
   */
  @Prop() invalidFormText: string;

  /**
   * state of the signup process
   */
  @State() signUpState: SignUpState;

  /**
   * state of the form
   */
  @State() formState: FormState = FormState.VALID;

  /**
   * state of the button
   */
  @State() buttonCtaState: ButtonState = ButtonState.DISABLED;

  /**
   * email value of the input field
   */
  emailValue: string;

  /**
   * email input validation delay handler reference
   */
  emailInputDelayReference: number;

  /**
   * Event thrown if the newsletter-signup was successful
   *
   * @docs
   * @event on:newsletter-signup:successful
   */
  @Event({
    eventName: 'on:newsletter-signup:successful',
  })
  onNewsletterSignupSuccess: EventEmitter;

  /**
   * Event thrown if the newsletter-signup failed
   *
   * @docs
   * @event on:newsletter-signup:failure
   */
  @Event({
    eventName: 'on:newsletter-signup:failure',
  })
  onNewsletterSignupFailure: EventEmitter;

  /**
   * Event thrown if the newsletter-signup throws an error
   *
   * @docs
   * @event on:newsletter-signup:error
   */
  @Event({
    eventName: 'on:newsletter-signup:error',
  })
  onNewsletterSignupError: EventEmitter;

  /**
   * signUp
   */
  async signUp(event: Event) {
    event.preventDefault();

    this.signUpState = SignUpState.INITIAL;

    if (!this.isFormValid()) {
      return;
    }

    this.signUpState = SignUpState.LOAD;

    const properties: {
      locale?: string;
      language?: string;
      referrer: string;
      kid: string;
      email: string;
    } = {
      email: this.emailValue,
      kid: this.keyAccountId,
      referrer: window.location.href,
    };

    if (this.referrer) {
      properties.referrer = this.referrer;
    }

    if (this.language) {
      properties.language = this.language;
    }

    if (this.locale) {
      properties.locale = this.locale;
    }

    const newsletterApiOptions: UtilNewsletterApiOptions = {};
    if (this.newsletterApiEndpoint) {
      newsletterApiOptions.endpointUrl = this.newsletterApiEndpoint;
    }

    const newsletterApi = new UtilNewsletterApi(newsletterApiOptions),
      result = await newsletterApi.signIn(properties);

    if (result.state == STATES.ERROR) {
      this.signUpState = SignUpState.FAILED;
      /* emit the event - for tracking and things */
      this.emitError(this.eventEmitData());
      return;
    }

    if (result.state == STATES.FAILURE) {
      this.signUpState = SignUpState.INITIAL;
      this.formState = FormState.INVALID;
      this.buttonCtaState = ButtonState.DISABLED;
      /* emit the event - for tracking and things */
      this.emitFailure(this.eventEmitData());
      return;
    }

    this.signUpState = SignUpState.SUCCESSFUL;
    /* emit the event - for tracking and things */
    this.emitSuccess(this.eventEmitData());
  }

  /**
   * signUpRetry
   */
  signUpRetry() {
    this.signUpState = SignUpState.INITIAL;
  }

  /**
   * Triggers on EVERY change on the input field (onInput).
   */
  emailChanged(event: Event) {
    const emailInput = event.target as HTMLInputElement;
    this.emailValue = emailInput.value;

    this.buttonCtaState =
      this.isEmailInputValid() && this.isEmailInputNotEmpty() ? ButtonState.ENABLED : ButtonState.DISABLED;

    this.formState = FormState.VALID;
    this.handleEmailInputDelay(() => {
      this.isFormValid();
    });
  }

  /**
   * Called once just after the component is first connected to the DOM.
   */
  componentWillLoad() {
    this.signUpState = SignUpState.INITIAL;
  }

  /**
   * render
   */
  render() {
    return <NewsletterSignupV2Template controller={this} />;
  }

  /**
   * EventEmitData
   */
  private eventEmitData(): NewsletterSignupEventEmitData {
    return {
      element: this.element,
      usage: 'default',
    };
  }

  /**
   * emitError (type safe)
   */
  private emitError(emitData: NewsletterSignupEventEmitData) {
    this.onNewsletterSignupError.emit(emitData);
  }

  /**
   * emitFailure (type safe)
   */
  private emitFailure(emitData: NewsletterSignupEventEmitData) {
    this.onNewsletterSignupFailure.emit(emitData);
  }

  /**
   * emitSuccess (type safe)
   */
  private emitSuccess(emitData: NewsletterSignupEventEmitData) {
    this.onNewsletterSignupSuccess.emit(emitData);
  }

  /**
   * isFormValid
   * @private
   */
  private isFormValid() {
    this.formState = FormState.VALID;

    const isEmailInputValid = this.isEmailInputValid();

    if (isEmailInputValid) {
      return true;
    }

    // an empty input field is considered valid because we do not want to show UI error states for an empty field
    if (this.isEmailInputNotEmpty()) {
      this.formState = FormState.INVALID;
    }

    return false;
  }

  /**
   * isEmailInputValid
   * @private
   */
  private isEmailInputValid(): boolean {
    const isNullOrEmpty = this.isEmailInputNullOrEmpty();

    if (isNullOrEmpty) {
      return true;
    }

    const input = this.getEmailInput();

    if (typeof input.checkValidity === 'function') {
      return input.checkValidity();
    }

    // if the input does not have a checkValidity function, we assume the input is valid
    return true;
  }

  /**
   * isEmailInputNullOrEmpty
   * @private
   */
  private isEmailInputNullOrEmpty(): boolean {
    const input = this.getEmailInput();

    if (!input) {
      return true;
    }

    return input.value.length === 0;
  }

  private isEmailInputNotEmpty(): boolean {
    return !this.isEmailInputNullOrEmpty();
  }

  /**
   * getEmailInput
   * @private
   */
  private getEmailInput() {
    return this.getContent().querySelector<HTMLInputElement>('input[name="email"]');
  }

  /**
   * handleEmailInputDelay
   * @param callback
   * @private
   */
  private handleEmailInputDelay(callback: TimerHandler) {
    if (this.emailInputDelayReference) {
      clearTimeout(this.emailInputDelayReference);
    }

    this.emailInputDelayReference = setTimeout(callback, NewsletterSignupV2Component.EMAIL_INPUT_DELAY);
  }
}

/**
 * mixin abstract functions
 */
Object.setPrototypeOf(NewsletterSignupV2Component.prototype, AbstractComponent.prototype);

export interface NewsletterSignupV2Component extends AbstractComponent {}
